/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useMemo } from 'react';
import cx from 'classnames';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import Title from '../AZCustomComponent/Title';
import azCommonStyles from '../../theme/globals.module.scss';
import styles from './FilterDesktop/styles.module.scss';
import type { RefinementType } from '@/types/filterTypes';
import { getSelectedRefinementLabels } from './utils/getSelectedRefinementLabels';
import { Text } from '@az/starc-ui';

type Props = {
  title: string;
  classes?: { root?: string; firstTextDesktop?: string; selectedLabels?: string };
  selectedRefinements?: RefinementType[];
  isExpanded?: boolean;
  dimensionName?: string;
};

const FilterSubHeading = ({
  title,
  classes = {},
  selectedRefinements,
  isExpanded,
  dimensionName,
}: Props): React.ReactElement => {
  const selectedLabelsText = useMemo(
    () => (selectedRefinements ? getSelectedRefinementLabels(selectedRefinements) : null),
    [selectedRefinements],
  );

  const showSelectedLabels = !isExpanded && selectedLabelsText;

  if (useMediaQuery((theme) => theme.breakpoints.down('md'))) {
    return (
      <div className={styles.mobileHeadingWrapper}>
        <div>
          <span
            className={cx(azCommonStyles['az-overline'], styles.mobileSubHeading, {
              [classes.root || '']: classes.root,
            })}
            data-testid={`filterSubHeading-${title}`}
          >
            {title}
          </span>
        </div>
        {showSelectedLabels && (
          <Text
            className={classes.selectedLabels}
            data-testid={`selected-${dimensionName}`}
            variant="small-body"
            lineHeight="140"
          >
            {selectedLabelsText}
          </Text>
        )}
      </div>
    );
  } else {
    const testId = title.toLowerCase().replace(/\s/g, '-');
    return (
      <div>
        <Title
          dataTestIdFirstText={`filter-header-${testId}`}
          firstText={title}
          variant="xxs"
          swapStyle
          firstTextClass={cx(azCommonStyles['az-title-5-medium'], {
            [classes.firstTextDesktop || '']: classes.firstTextDesktop,
          })}
          stylesClass={cx(styles.subheading, {
            [classes.root || '']: classes.root,
          })}
          overRuleH1="span"
          id={`filterSubHeading-${title}`}
        />
        {showSelectedLabels && (
          <Text
            className={classes.selectedLabels}
            data-testid={`selected-${dimensionName}`}
            variant="small-body"
          >
            {selectedLabelsText}
          </Text>
        )}
      </div>
    );
  }
};

export default FilterSubHeading;
